import { MapPin, Phone, Printer } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';
import type {
  ContactPointSummaryV2,
  ProviderServiceSearchResultV2,
} from '@/services/api/generated/providerDirectoryV2IdentityService';

interface ProviderCardProps {
  result: ProviderServiceSearchResultV2;
  className?: string;
}

const formatAddress = (
  loc?: ProviderServiceSearchResultV2['clinical_location'],
) => {
  if (!loc?.address) return null;
  const addr = loc.address;
  const parts = [
    addr.address_line1,
    addr.city,
    addr.state,
    addr.postal_code,
  ].filter(Boolean);
  return parts.length > 0
    ? parts.join(', ')
    : (addr.formatted_address ?? null);
};

/** Derive a verification label from the search result. */
const deriveVerificationState = (
  result: ProviderServiceSearchResultV2,
): 'verified' | 'pending' | 'unverified' => {
  // Use destination status or verified_contact_count as primary signals
  if (
    result.records_destination_status === 'VERIFIED' ||
    result.bills_destination_status === 'VERIFIED' ||
    (result.verified_contact_count && result.verified_contact_count > 0)
  )
    return 'verified';

  // Fallback: check best_contacts for verified_at
  const contacts = [
    result.best_contacts?.fax,
    result.best_contacts?.email,
    result.best_contacts?.phone,
  ].filter(Boolean) as ContactPointSummaryV2[];

  if (contacts.some((c) => c.verified_at)) return 'verified';
  if (contacts.length > 0) return 'pending';
  return 'unverified';
};

/** Relative time from an ISO date string */
const relativeTime = (iso: string | null | undefined) => {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days < 1) return 'today';
  if (days === 1) return '1d ago';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(months / 12);
  return `${years}y ago`;
};

const VERIFICATION_STYLES = {
  verified: { dotClass: 'bg-success', textClass: 'text-success', label: 'Verified' },
  pending: { dotClass: 'bg-warning', textClass: 'text-warning', label: 'Pending' },
  unverified: { dotClass: 'bg-zinc-400', textClass: 'text-info-light', label: 'Unverified' },
} as const;

export const ProviderCard = ({
  result,
  className,
}: ProviderCardProps) => {
  const navigate = useNavigate();
  const address = formatAddress(result.clinical_location);
  const verificationState = deriveVerificationState(result);
  const verificationStyle = VERIFICATION_STYLES[verificationState];

  const service = result.provider_service;
  const org = result.organization;
  const location = result.clinical_location;

  const fax = result.best_contacts?.fax;
  const phone = result.best_contacts?.phone;

  // Build metadata line items
  const metaParts: string[] = [];

  // Verification recency from the first verified contact
  const verifiedContacts = [fax, phone, result.best_contacts?.email].filter(
    (c): c is ContactPointSummaryV2 => Boolean(c?.verified_at),
  );
  if (verifiedContacts.length > 0 && verifiedContacts[0].verified_at) {
    const ago = relativeTime(verifiedContacts[0].verified_at);
    if (ago) metaParts.push(`Verified ${ago}`);
  }

  // Verification source
  if (verifiedContacts.length > 0 && verifiedContacts[0].verification_source) {
    metaParts.push(
      `Source: ${verifiedContacts[0].verification_source.replace(/_/g, ' ').toLowerCase()}`,
    );
  }

  // Active request plan
  if (location?.has_active_request_plan) {
    metaParts.push('Active request plan');
  }

  const handleClick = () => {
    navigate(`/provider-directory/${service.id}`);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'w-full text-left rounded-xl border border-border bg-background p-5 transition-all',
        'hover:shadow-md hover:border-primary/30 cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        className,
      )}
    >
      {/* Row 1: Name + Verification dot */}
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-base font-semibold text-info-dark">
          {service.name}
        </h3>
        <span className={cn('inline-flex items-center gap-1 text-[11px] font-medium', verificationStyle.textClass)}>
          <span className={cn('h-[7px] w-[7px] rounded-full shrink-0', verificationStyle.dotClass)} />
          {verificationStyle.label}
        </span>
      </div>

      {/* Row 2: Address · Organization */}
      {(address || org) && (
        <p className="mt-1.5 flex items-center gap-1 text-[13px] text-info">
          <MapPin size={14} className="shrink-0 text-info-light" />
          {address && <span>{address}</span>}
          {address && org && <span className="text-info-light">·</span>}
          {org && (
            <span className="text-primary font-medium">{org.name}</span>
          )}
        </p>
      )}

      {/* Row 3: Contact values + badges */}
      <div className="mt-2 flex items-center flex-wrap gap-x-3 gap-y-1">
        {/* Fax */}
        {fax?.value ? (
          <span className={cn(
            'inline-flex items-center gap-1 text-xs font-medium',
            fax.verified_at ? 'text-success' : 'text-info',
          )}>
            <Printer size={13} className="shrink-0" />
            {fax.value}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-info-light">
            <Printer size={13} className="shrink-0" />
            No fax on file
          </span>
        )}

        {/* Phone */}
        {phone?.value && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-info">
            <Phone size={13} className="shrink-0" />
            {phone.value}
          </span>
        )}

        {/* Service type badge */}
        {service.service_type && service.service_type !== 'GENERAL' && (
          <span className="inline-flex items-center rounded-full bg-primary-light px-2 py-0.5 text-[11px] font-medium text-info">
            {service.service_type.replace(/_/g, ' ')}
          </span>
        )}

        {/* Portal type badges */}
        {service.portal_type && service.portal_type.length > 0 ? (
          service.portal_type.map((pt) => (
            <span
              key={pt}
              className="inline-flex items-center rounded-full bg-primary-light px-2 py-0.5 text-[11px] font-medium text-primary"
            >
              {pt === 'chartswap' ? 'ChartSwap' : pt === 'smart_request' ? 'SmartRequest' : pt === 'mro' ? 'MRO' : pt}
            </span>
          ))
        ) : service.portal_eligible ? (
          <span className="inline-flex items-center rounded-full bg-primary-light px-2 py-0.5 text-[11px] font-medium text-primary">
            Portal eligible
          </span>
        ) : null}
      </div>

      {/* Row 4: Metadata / provenance line */}
      {metaParts.length > 0 && (
        <p className="mt-2 text-xs text-zinc-500">
          {metaParts.join(' · ')}
        </p>
      )}
    </button>
  );
};
